import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ProductCard from './ProductCard';
import { useNavigate } from 'react-router-dom';
import './VendorCard.css'; // Import the new CSS file

const VendorCard = ({ vendor, orderType }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [statusText, setStatusText] = useState('');
  const PRODUCT_LIMIT = 30;

  // Filter out products without valid images and ensure description is preserved
  const validProducts = useMemo(() => {
    if (!vendor.products || !Array.isArray(vendor.products)) {
      return [];
    }
    
    // Filter products with valid images AND ensure description is preserved
    return vendor.products
      .filter(product => product.image && product.image !== '/placeholder-image.png')
      .map(product => ({
        ...product,
        // Explicitly ensure description exists
        description: product.description || '',
        strainDetails: product.strainDetails || '',
      }));
  }, [vendor.products]);

  const formatTime = (time) => {
    // Convert 24-hour time string (HH:MM) to 12-hour format with AM/PM
    const [hours] = time.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12} ${ampm}`;
  };

  const calculateVendorStatus = useCallback(() => {
    if (!vendor.businessHours) return { isOpen: false, text: 'Closed' };

    const now = new Date();
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const currentDay = days[now.getDay()];
    
    const currentTime = now.toLocaleTimeString('en-US', { 
      hour12: false, 
      hour: '2-digit', 
      minute: '2-digit'
    });

    const todayHours = vendor.businessHours[currentDay];
    if (!todayHours?.open || !todayHours?.close) {
      return { isOpen: false, text: 'Closed' };
    }

    const isCurrentlyOpen = currentTime >= todayHours.open && currentTime <= todayHours.close;
    
    if (isCurrentlyOpen) {
      return { 
        isOpen: true, 
        text: `Open until ${formatTime(todayHours.close)}` 
      };
    } else if (currentTime < todayHours.open) {
      return { 
        isOpen: false, 
        text: `Opens at ${formatTime(todayHours.open)}` 
      };
    } else {
      return { 
        isOpen: false, 
        text: 'Closed' 
      };
    }
  }, [vendor.businessHours]);

  useEffect(() => {
    const status = calculateVendorStatus();
    setIsOpen(status.isOpen);
    setStatusText(status.text);

    // Update status every minute
    const intervalId = setInterval(() => {
      const newStatus = calculateVendorStatus();
      setIsOpen(newStatus.isOpen);
      setStatusText(newStatus.text);
    }, 60000);

    return () => clearInterval(intervalId);
  }, [calculateVendorStatus]);

  const handleViewMenu = () => {
    navigate(`/vendor/${vendor._id}/menu`);
  };

  // Get the display products limited to PRODUCT_LIMIT
  const displayProducts = validProducts.slice(0, PRODUCT_LIMIT);
  
  // Debug log to verify products have descriptions
  console.log('Products in VendorCard:', displayProducts.map(p => 
    ({ id: p._id, name: p.name, hasDescription: !!p.description })
  ));

  return (
    <div className="vendor-card">
      <div className="vendor-header">
        <div className="vendor-info-container">
          <img
            src={vendor.logoUrl || '/placeholder-logo.png'}
            alt={vendor.dispensaryName}
            className="card-vendor-logo"
            onClick={handleViewMenu}
          />

          <div className="vendor-details">
            <h6 className="vendor-name" onClick={handleViewMenu}>
              {vendor.dispensaryName}
            </h6>
            <p className="vendor-info">
              {vendor.minOrder && vendor.minOrder > 0 ? `${vendor.minOrder}` : 'No'} minimum | {vendor.milesAway || 'N/A'} miles away
            </p>
            <div 
              className={`status-indicator ${isOpen ? 'open' : 'closed'}`}
            >
              <span className={`status-dot ${isOpen ? 'open' : 'closed'}`}></span>
              {statusText}
            </div>
          </div>
        </div>

        <div className="menu-button-container">
          <button
            className="menu-button"
            onClick={handleViewMenu}
          >
            Menu
          </button>
        </div>
      </div>

      <div className="products-preview">
        {displayProducts.length > 0 ? (
          <div className="products-container">
            {displayProducts.map((product) => (
              <div
                key={`${product._id}-${product.strain}-${product.amount}`}
                className="product-card-wrapper"
              >
                <ProductCard
                  product={product}
                  vendorName={vendor.dispensaryName}
                  logoUrl={vendor.logoUrl}
                  vendorId={vendor._id}
                  orderType={orderType}
                />
              </div>
            ))}
            {validProducts.length > PRODUCT_LIMIT && (
              <div className="view-more-container">
                <button
                  onClick={handleViewMenu}
                  className="btn view-more-button"
                >
                  View Full Menu
                  <br />
                  ({validProducts.length - PRODUCT_LIMIT} more items)
                </button>
              </div>
            )}
          </div>
        ) : (
          <p className="no-products">No matching products available</p>
        )}
      </div>
    </div>
  );
};

export default VendorCard;